import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Pencil, X, Check, FileText, List,
} from 'lucide-react'
import { useRewriteStore } from '@/stores/rewriteStore'
import { useAuthStore } from '@/stores/authStore'
import { rewriteApi } from '@/services/api'
import clsx from 'clsx'

// Hooks
import { useImageUpload } from '@/hooks/useImageUpload'
import { useGuidelineManager } from '@/hooks/useGuidelineManager'
import { useContentEditor } from '@/hooks/useContentEditor'

// Workspace components
import { ChannelTabs } from '@/components/rewrite/workspace/ChannelTabs'
import { SectionList, getChannelSections } from '@/components/rewrite/workspace/SectionList'
import { SourceTab } from '@/components/rewrite/workspace/SourceTab'
import { GuidelineTab } from '@/components/rewrite/workspace/GuidelineTab'
import { GenerationTab } from '@/components/rewrite/workspace/GenerationTab'
import { FinalizedTab } from '@/components/rewrite/workspace/FinalizedTab'
import { SourceInputModal } from '@/components/rewrite/workspace/SourceInputModal'

// Existing components
import { GenerationProgress } from '@/components/rewrite/GenerationProgress'
import { ImageConfirmModal } from '@/components/rewrite/ImageConfirmModal'

type DetailTab = 'source' | 'guideline' | 'generation' | 'finalized'

// 섹션 데이터를 문자열로 변환 (객체인 경우 구조화된 텍스트로)
function formatSectionData(data: unknown): string {
  if (!data) return ''
  if (typeof data === 'string') return data

  if (typeof data === 'object' && data !== null) {
    const lines: string[] = []
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        lines.push(`[${key}]`)
        value.forEach((item) => {
          if (typeof item === 'object') {
            lines.push(`  ${JSON.stringify(item)}`)
          } else {
            lines.push(`  • ${item}`)
          }
        })
      } else if (typeof value === 'object' && value !== null) {
        lines.push(`[${key}] ${JSON.stringify(value)}`)
      } else {
        lines.push(`[${key}] ${value}`)
      }
    }
    return lines.join('\n')
  }

  return String(data)
}

export default function RewriteCreatePage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  const {
    currentProject, sources, channels, contents, isGenerating, error,
    fetchProject, fetchSources, fetchChannels, fetchContents,
    createProject, createSource, generate, updateContent, updateProject,
    rewriteSection, restoreVersion, clearError,
  } = useRewriteStore()

  const [sourceText, setSourceText] = useState('')
  const [sourceTitle, setSourceTitle] = useState('')
  const [selectedChannels, setSelectedChannels] = useState<string[]>([])
  const [projectTitle, setProjectTitle] = useState(() => {
    const now = new Date()
    const dt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`
    return `임시 프로젝트 ${dt}`
  })
  const [isTitleDefault, setIsTitleDefault] = useState(true)
  const [editingTitle, setEditingTitle] = useState(false)
  const [showChannelPicker, setShowChannelPicker] = useState(false)
  const [selectedModel, setSelectedModel] = useState('claude-sonnet-4-20250514')
  const [generationStep, setGenerationStep] = useState(0)
  const [models, setModels] = useState<Array<{ id: string; name: string; description: string }>>([])

  // New workspace state
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null)
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)
  const [activeTab, setActiveTab] = useState<DetailTab>('source')
  const [showSourceModal, setShowSourceModal] = useState(false)
  const [sectionSourceTexts, setSectionSourceTexts] = useState<Record<string, string>>({})
  const [showMobileSections, setShowMobileSections] = useState(false)

  // Custom hooks
  const imageUpload = useImageUpload({
    projectId,
    projectTitle,
    createProject,
    onTextExtracted: useCallback((content: string, title?: string) => {
      setSourceText(prev => prev ? prev + '\n\n' + content : content)
      if (title && !sourceTitle) setSourceTitle(title)
    }, [sourceTitle]),
  })

  const guidelines = useGuidelineManager(selectedChannels)

  const getGuidelineContext = useCallback(() => {
    const { guidelineIds, customGuidelines: customGL } = guidelines.getGuidelineParams()
    // 현재 활성 채널의 가이드라인만 추출 (flat array)
    const channelGuidelineIds = activeChannelId && guidelineIds?.[activeChannelId]
      ? guidelineIds[activeChannelId]
      : undefined
    const channelCustomGuidelines = activeChannelId && customGL?.[activeChannelId]
      ? customGL[activeChannelId]
      : undefined
    return {
      guidelineIds: channelGuidelineIds,
      customGuidelines: channelCustomGuidelines,
      model: selectedModel,
    }
  }, [guidelines, activeChannelId, selectedModel])

  const editor = useContentEditor({
    contents,
    updateContent,
    rewriteSection,
    restoreVersion,
    getGuidelineContext,
  })

  // Init
  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    fetchChannels()
    if (projectId) {
      fetchProject(projectId)
      fetchSources(projectId)
      fetchContents(projectId)
    }
  }, [projectId, isAuthenticated])

  useEffect(() => {
    if (projectId && sources.length > 0 && !sourceText) {
      const src = sources[0]
      setSourceText(src.content)
      if (src.title) setSourceTitle(src.title)
    }
  }, [projectId, sources])

  useEffect(() => {
    if (currentProject) {
      setProjectTitle(currentProject.title)
      setIsTitleDefault(false)
    }
  }, [currentProject])

  useEffect(() => {
    rewriteApi.generate.models().then(res => {
      if (res.success && res.data) setModels(res.data)
    })
  }, [])

  // Auto-select first channel
  useEffect(() => {
    if (selectedChannels.length > 0 && !activeChannelId) {
      setActiveChannelId(selectedChannels[0])
    }
    if (activeChannelId && !selectedChannels.includes(activeChannelId) && selectedChannels.length > 0) {
      setActiveChannelId(selectedChannels[0])
    }
  }, [selectedChannels, activeChannelId])

  // Reset section index when channel changes
  useEffect(() => {
    setActiveSectionIndex(0)
  }, [activeChannelId])

  const handleToggleChannel = useCallback((channelId: string) => {
    setSelectedChannels(prev =>
      prev.includes(channelId) ? prev.filter(id => id !== channelId) : [...prev, channelId]
    )
  }, [])

  const handleCreateAndGenerate = useCallback(async () => {
    if (!sourceText.trim() || selectedChannels.length === 0 || isGenerating) return

    let pid = projectId
    if (!pid) {
      const project = await createProject(projectTitle || '새 프로젝트')
      if (!project) return
      pid = project.id
    }

    let source = sources.length > 0 ? sources[0] : null
    if (!source || source.content !== sourceText) {
      source = await createSource(pid, sourceText, sourceTitle || undefined)
      if (!source) return
    }

    const { guidelineIds, customGuidelines } = guidelines.getGuidelineParams()

    setGenerationStep(1)
    const stepTimer1 = setTimeout(() => setGenerationStep(2), 3000)
    const stepTimer2 = setTimeout(() => setGenerationStep(3), 6000)
    try {
      const success = await generate(source.id, selectedChannels, pid, selectedModel, guidelineIds, customGuidelines)
      clearTimeout(stepTimer1)
      clearTimeout(stepTimer2)
      if (success) {
        await fetchSources(pid)
        setActiveTab('generation')
      }
    } catch {
      clearTimeout(stepTimer1)
      clearTimeout(stepTimer2)
    } finally {
      setGenerationStep(0)
    }
  }, [sourceText, selectedChannels, projectId, projectTitle, sourceTitle, selectedModel, isGenerating, sources, guidelines, createProject, createSource, generate, fetchSources])

  const handleSaveProjectTitle = useCallback(async () => {
    if (projectTitle.trim()) {
      setIsTitleDefault(false)
      if (projectId) {
        await updateProject(projectId, { title: projectTitle.trim() })
      }
    }
    setEditingTitle(false)
  }, [projectId, projectTitle, updateProject])

  const handleUpdateSectionSource = useCallback((text: string) => {
    const key = `${activeChannelId}-${activeSectionIndex}`
    setSectionSourceTexts(prev => ({ ...prev, [key]: text }))
  }, [activeChannelId, activeSectionIndex])

  // Current section info
  const activeChannel = useMemo(() => channels.find(c => c.id === activeChannelId), [channels, activeChannelId])
  const currentSections = useMemo(() => activeChannel ? getChannelSections(activeChannel) : [], [activeChannel])
  const currentSection = currentSections[activeSectionIndex]
  const sectionSourceKey = `${activeChannelId}-${activeSectionIndex}`

  const analysisRaw = sources.length > 0 ? sources[0].analysis : undefined

  const TABS: { key: DetailTab; label: string }[] = [
    { key: 'source', label: '원문' },
    { key: 'guideline', label: '가이드라인' },
    { key: 'generation', label: 'AI생성' },
    { key: 'finalized', label: '확정' },
  ]

  return (
    <div className="min-h-[calc(100vh-56px)] flex flex-col">
      {/* Header */}
      <div className="px-4 md:px-6 py-3 border-b border-border-subtle bg-bg-secondary/50">
        <div className="flex items-center justify-between max-w-[1920px] mx-auto gap-3">
          {/* Left: Project name (can shrink/truncate) */}
          <div className="flex items-center min-w-0">
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  value={projectTitle}
                  onChange={(e) => setProjectTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveProjectTitle()}
                  className="text-lg font-bold bg-bg-elevated border border-border-subtle rounded px-2 py-0.5 min-w-[200px]"
                  autoFocus
                />
                <button onClick={handleSaveProjectTitle} className="p-1 text-primary"><Check className="w-4 h-4" /></button>
                <button onClick={() => setEditingTitle(false)} className="p-1 text-text-tertiary"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button
                onClick={() => { setEditingTitle(true); if (isTitleDefault) setProjectTitle('') }}
                className={clsx(
                  'flex items-center gap-2 min-w-0 group rounded-lg px-2 py-1 -mx-2 -my-1 transition-all',
                  isTitleDefault
                    ? 'border border-dashed border-primary/40 hover:border-primary hover:bg-primary/5'
                    : 'hover:bg-bg-elevated'
                )}
              >
                <h1 className={clsx(
                  'text-lg font-bold truncate',
                  isTitleDefault && 'text-text-tertiary italic'
                )}>
                  {projectTitle}
                </h1>
                <Pencil className={clsx(
                  'w-3.5 h-3.5 flex-shrink-0 transition-all',
                  isTitleDefault ? 'text-primary animate-pulse' : 'text-text-tertiary group-hover:text-text-primary'
                )} />
              </button>
            )}
          </div>

          {/* Center: Action buttons (won't shrink, always visible) */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="h-5 w-px bg-border-subtle" />

            {/* 원문등록 */}
            <button
              onClick={() => setShowSourceModal(true)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                sourceText
                  ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                  : 'bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20'
              )}
            >
              <FileText className="w-3.5 h-3.5" />
              {sourceText ? `원문 ${sourceText.length.toLocaleString()}자` : '원문등록'}
            </button>

            {/* 채널 선택 */}
            <div className="relative">
              <button
                onClick={() => setShowChannelPicker(!showChannelPicker)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                  selectedChannels.length > 0
                    ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                    : 'bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20'
                )}
              >
                채널 {selectedChannels.length > 0
                  ? `(${selectedChannels.length}개)`
                  : '선택'}
              </button>
              {showChannelPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowChannelPicker(false)} />
                  <div className="absolute left-0 top-full mt-1 bg-bg-primary border border-border-subtle rounded-lg shadow-xl p-2 min-w-[220px] z-20">
                    {channels.map(ch => (
                      <label key={ch.id} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-bg-elevated cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedChannels.includes(ch.id)}
                          onChange={() => handleToggleChannel(ch.id)}
                          className="accent-primary"
                        />
                        <span className="text-sm">{ch.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right: model + navigation */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {models.length > 0 && (
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="bg-bg-elevated border border-border-subtle rounded-lg px-2 py-1.5 text-xs"
              >
                {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
            <button onClick={() => navigate('/rewrite')} className="btn btn-ghost text-sm">프로젝트 목록</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-4 md:mx-6 mt-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg flex justify-between items-center text-sm">
          <span>{error}</span>
          <button onClick={clearError}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Channel Tabs */}
      <ChannelTabs
        channels={channels}
        selectedChannels={selectedChannels}
        activeChannelId={activeChannelId}
        isGenerating={isGenerating}
        onSelectChannel={setActiveChannelId}
        onGenerate={handleCreateAndGenerate}
      />

      {/* Generation progress */}
      {isGenerating && generationStep > 0 && (
        <GenerationProgress generationStep={generationStep} channelCount={selectedChannels.length} />
      )}

      {/* Main content area: Section list (30%) + Detail (70%) */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Section list */}
        <div className={clsx(
          'border-r border-border-subtle flex-shrink-0 overflow-hidden',
          'hidden lg:flex lg:flex-col lg:w-[250px]'
        )}>
          <SectionList
            channels={channels}
            activeChannelId={activeChannelId}
            activeSectionIndex={activeSectionIndex}
            onSelectSection={setActiveSectionIndex}
          />

        </div>

        {/* Mobile section toggle */}
        <button
          onClick={() => setShowMobileSections(!showMobileSections)}
          className="lg:hidden fixed bottom-4 left-4 z-10 bg-primary text-white p-3 rounded-full shadow-lg"
        >
          <List className="w-5 h-5" />
        </button>

        {/* Mobile section drawer */}
        {showMobileSections && (
          <div className="lg:hidden fixed inset-0 z-20">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileSections(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-[250px] bg-bg-primary border-r border-border-subtle">
              <SectionList
                channels={channels}
                activeChannelId={activeChannelId}
                activeSectionIndex={activeSectionIndex}
                onSelectSection={(idx) => { setActiveSectionIndex(idx); setShowMobileSections(false) }}
              />
            </div>
          </div>
        )}

        {/* Right: Detail panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Detail tabs */}
          <div className="flex items-center border-b border-border-subtle bg-bg-secondary/20 px-4">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={clsx(
                  'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all',
                  activeTab === t.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-tertiary hover:text-text-primary'
                )}
              >
                {t.label}
              </button>
            ))}
            {currentSection && (
              <span className="ml-auto text-xs text-text-tertiary">
                {currentSection.title}
              </span>
            )}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            {!activeChannelId || !currentSection ? (
              <div className="flex-1 flex flex-col items-center justify-center text-text-tertiary p-6 h-full">
                <FileText className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-lg font-medium mb-1">채널과 섹션을 선택하세요</p>
                <p className="text-sm">헤더에서 채널을 선택하고 좌측에서 섹션을 선택하세요</p>
              </div>
            ) : (
              <>
                {activeTab === 'source' && (
                  <SourceTab
                    sourceText={sourceText}
                    analysisRaw={analysisRaw}
                    sectionTitle={currentSection.title}
                    sectionSourceText={sectionSourceTexts[sectionSourceKey] || ''}
                    onUpdateSectionSource={handleUpdateSectionSource}
                  />
                )}

                {activeTab === 'guideline' && (
                  <GuidelineTab
                    channel={activeChannel}
                    sectionType={currentSection.type}
                    sectionIndex={activeSectionIndex}
                    channelGuidelines={guidelines.channelGuidelines}
                    selectedGuidelineIds={guidelines.selectedGuidelineIds}
                    guidelineEdits={guidelines.guidelineEdits}
                    editingGuidelineId={guidelines.editingGuidelineId}
                    customGuidelines={guidelines.customGuidelines}
                    customGuidelineInput={guidelines.customGuidelineInput}
                    loadingGuidelines={guidelines.loadingGuidelines}
                    onSelectAll={guidelines.selectAll}
                    onDeselectAll={guidelines.deselectAll}
                    onToggleGuideline={guidelines.toggleGuideline}
                    onStartEditGuideline={guidelines.startEditGuideline}
                    onStopEditGuideline={guidelines.stopEditGuideline}
                    onUpdateGuidelineEdit={guidelines.updateGuidelineEdit}
                    onResetGuidelineEdit={guidelines.resetGuidelineEdit}
                    onAddCustomGuideline={guidelines.addCustomGuideline}
                    onRemoveCustomGuideline={guidelines.removeCustomGuideline}
                    onSetCustomInput={guidelines.setCustomInput}
                  />
                )}

                {activeTab === 'generation' && (
                  <GenerationTab
                    contents={contents}
                    channels={channels}
                    activeChannelId={activeChannelId}
                    activeSectionIndex={activeSectionIndex}
                    editingSection={editor.editingSection}
                    editText={editor.editText}
                    rewritingSection={editor.rewritingSection}
                    copiedId={editor.copiedId}
                    onEditTextChange={editor.setEditText}
                    onSave={editor.handleSaveSection}
                    onRewrite={editor.handleRewrite}
                    onCopy={editor.handleCopy}
                    onStartEdit={editor.startEditing}
                    onCancelEdit={editor.cancelEditing}
                  />
                )}

                {activeTab === 'finalized' && (
                  <FinalizedTab
                    contents={contents}
                    activeChannelId={activeChannelId}
                    activeSectionIndex={activeSectionIndex}
                    onUpdateContent={updateContent}
                    copiedId={editor.copiedId}
                    onCopy={editor.handleCopy}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Source Input Modal */}
      <SourceInputModal
        open={showSourceModal}
        sourceText={sourceText}
        sourceTitle={sourceTitle}
        uploadingImage={imageUpload.uploadingImage}
        imageMethod={imageUpload.imageMethod}
        imageModel={imageUpload.imageModel}
        selectedChannels={selectedChannels}
        onSetSourceText={setSourceText}
        onSetSourceTitle={setSourceTitle}
        onImageUpload={(file) => imageUpload.handleImageSelected(file)}
        onSetImageMethod={imageUpload.setImageMethod}
        onSetImageModel={imageUpload.setImageModel}
        onSectionSourcesExtracted={(extractedSections) => {
          // Store extracted sections keyed by unique section key
          if (!activeChannelId) return
          const channelSections = currentSections
          // 고유 섹션 키 생성 (중복 타입은 card_1, card_2, ... 형태)
          const typeTotal: Record<string, number> = {}
          channelSections.forEach(s => { typeTotal[s.type] = (typeTotal[s.type] || 0) + 1 })
          const typeCounts: Record<string, number> = {}
          const sectionKeyMap = channelSections.map((s, i) => {
            let uniqueKey: string
            if (typeTotal[s.type] === 1) {
              uniqueKey = s.type
            } else {
              typeCounts[s.type] = (typeCounts[s.type] || 0) + 1
              uniqueKey = `${s.type}_${typeCounts[s.type]}`
            }
            return { uniqueKey, type: s.type, index: i }
          })

          Object.entries(extractedSections).forEach(([extractedKey, data]) => {
            // 고유 키로 매핑 시도, 실패시 타입으로 매핑 (구 형식 호환)
            let match = sectionKeyMap.find(s => s.uniqueKey === extractedKey)
            if (!match) {
              match = sectionKeyMap.find(s => s.type === extractedKey)
            }
            if (match) {
              const key = `${activeChannelId}-${match.index}`
              const textValue = formatSectionData(data)
              setSectionSourceTexts(prev => ({ ...prev, [key]: textValue }))
            }
          })
        }}
        onClose={() => setShowSourceModal(false)}
      />

      {/* Image Confirm Modal */}
      {imageUpload.pendingImage && (
        <ImageConfirmModal
          pendingImage={imageUpload.pendingImage}
          imageMethod={imageUpload.imageMethod}
          imageModel={imageUpload.imageModel}
          onSetMethod={imageUpload.setImageMethod}
          onSetModel={imageUpload.setImageModel}
          onConfirm={imageUpload.handleImageConfirm}
          onCancel={imageUpload.handleImageCancel}
        />
      )}
    </div>
  )
}
